import { useState } from 'react'
import { Download } from 'lucide-react'
import { Modal } from '../Modal'
import { cn } from '../../lib/utils'
import { useInstallPrompt } from './useInstallPrompt'
import { InstallSteps } from './InstallSteps'
import { InstallTexts, resolveTexts } from './install.types'

/**
 * AP-17 (Baustein A + B), seit AP-17-E geteilt von ERP und Stempel-PWA.
 *
 * Prominenter „App installieren"-Button. Verhalten je Plattform:
 *  - Chrome/Edge/Android (`canInstall`): löst den nativen Ein-Klick-Install
 *    (`beforeinstallprompt.prompt()`) aus – kein Browser-Menü nötig.
 *  - iOS-Safari / Firefox / sonstige (kein `beforeinstallprompt`): öffnet ein
 *    geführtes Anleitungs-Modal, da Apple/Firefox keinen Auto-Install erlauben.
 *  - Bereits installiert (`isInstalled`): rendert nichts.
 */
export function InstallButton({
  texts,
  className,
}: {
  texts: InstallTexts
  className?: string
}) {
  const { canInstall, install, isInstalled, platform } = useInstallPrompt()
  const [guideOpen, setGuideOpen] = useState(false)
  const { appName, installLabel } = resolveTexts(texts)

  // Im Standalone-Modus (bereits installiert) ist der Button überflüssig.
  if (isInstalled) return null

  async function handleClick() {
    if (canInstall) {
      await install()
    } else {
      setGuideOpen(true)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-lg border border-primary/30',
          'bg-primary/10 px-4 py-2.5 text-sm font-semibold text-primary',
          'hover:bg-primary/15 active:scale-[0.98] transition-all',
          className,
        )}
      >
        <Download size={18} />
        {installLabel}
      </button>

      <Modal
        isOpen={guideOpen}
        onClose={() => setGuideOpen(false)}
        title="App installieren"
        size="sm"
      >
        <p className="mb-4 text-xs text-muted-foreground leading-relaxed">
          {platform === 'ios'
            ? `Auf dem iPhone/iPad wird ${appName} über das Safari-Menü zum Home-Bildschirm hinzugefügt:`
            : `So installierst du ${appName} auf deinem Gerät:`}
        </p>
        <InstallSteps platform={platform} texts={texts} />
      </Modal>
    </>
  )
}
